import React, { useEffect, useState, useRef, useCallback } from 'react';
import { TextField, Text, makeStyles, useTheme, Spinner, SpinnerSize, Stack, TooltipHost } from '@fluentui/react';
import { constants } from '../../Styles/constants';
import { PeriodDay } from '../../types/PeriodDay';
import { convertHoursToProcent, getCapacityPrDay } from './Cell.helpers';
import { roundToNearestValueStep } from '../../Mobile/mobileUtils';
import { useConfigValuesState } from '../../hooks/useConfigValue';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../api/api';
import { isValidNumber, parseNumber } from './Cell.helpers';
import { GetNumberSep } from '../Utils';

interface IProps {
    value: string | number;
    periodDays: PeriodDay[];
    assignmentId: string;
    taskId?: string;
    workTypeId?: string;
    readonly?: boolean;
    readonlyReason?: string;
    onRemainingWorkUpdate?: (assignmentId: string, newValue: number) => void;
}

export const EditableRemainingWorkCell: React.FC<IProps> = props => {
    const {
        initialData: {
            configuration: { gridInputType, timeEntryRoundingInterval, capacityDefault },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const theme = useTheme();
    const styles = getStyles();
    const textFieldRef = useRef<any>();

    const [celltext, setCelltext] = useState('');
    const [cellnumber, setCellnumber] = useState<number>(0);
    const [cellnumberlastsaved, setCellnumberlastsaved] = useState<number>(0);
    const [cellIsError, setCellIsError] = useState<string>('');
    const [textinfocus, setTextinfocus] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [dataSaved, setDataSaved] = useState<boolean>(false);

    const userCapacity = user.availability ?? capacityDefault;

    useEffect(() => {
        if (Number.isNaN(Number(props.value))) {
            setCelltext('');
            setCellnumber(0);
            setCellnumberlastsaved(0);
            setLoaded(true);
            return;
        }

        const numValue = Number(props.value);
        const formattedCellValue =
            gridInputType === 'percent' ? convertHoursToProcent(numValue, props.periodDays.length, getCapacityPrDay(props.periodDays, userCapacity)) : numValue;
        const roundedCellValue = roundToNearestValueStep(formattedCellValue, timeEntryRoundingInterval);

        setCelltext(`${roundedCellValue}`);
        setCellnumber(numValue);
        setCellnumberlastsaved(numValue);
        setLoaded(true);
    }, [props.value, gridInputType, props.periodDays, userCapacity, timeEntryRoundingInterval]);

    const { mutate: updateRemainingWork, isLoading } = useMutation({
        mutationFn: async (remainingWork: number) => {
            return ApiCalls.usersAssignmentsRemainingWorkUpdate({
                assignmentId: props.assignmentId,
                remainingWork: remainingWork,
                taskId: props.taskId,
                workTypeId: props.workTypeId,
            });
        },
        onSuccess: () => {
            setDataSaved(false);
            // Notify parent of the change
            if (props.onRemainingWorkUpdate) {
                props.onRemainingWorkUpdate(props.assignmentId, cellnumber);
                // set local cell state
                setCelltext(cellnumber.toString());
                setCellnumberlastsaved(cellnumber);
            }
        },
        onError: error => {
            console.error('Error updating remaining work:', error);
            setCellIsError('There was an error saving the data.');
            setDataSaved(false);
        },
    });

    const isValid = useCallback(
        (value: string) => {
            if (gridInputType === 'percent' && userCapacity === 0) {
                setCellIsError('No capacity defined for user');
                return '';
            }

            const parsed = value.replace(/,/g, '.');
            const newVal =
                gridInputType === 'percent'
                    ? `${convertProcentToHours(Number(parsed), props.periodDays.length, getCapacityPrDay(props.periodDays, userCapacity))}`
                    : parsed;

            if (isValidNumber(newVal)) {
                const newValNum = parseFloat(newVal);
                if (newValNum < 0) {
                    setCellIsError('Value cannot be negative');
                    return '';
                }
                setCellIsError('');
                return '';
            } else {
                setCellIsError('Invalid number format');
                return '';
            }
        },
        [gridInputType, props.periodDays, userCapacity],
    );

    const onCellUpdated = useCallback(
        (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
            if (isAllowedKey(e)) {
                const hours =
                    gridInputType === 'percent'
                        ? convertProcentToHours(Number(text), props.periodDays.length, getCapacityPrDay(props.periodDays, userCapacity))
                        : text;

                isValid(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
                setCelltext(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
                setCellnumber(parseNumber(hours.toString().replace(/\,/g, '.')));
            }
        },
        [gridInputType, isValid, props.periodDays, userCapacity],
    );

    const onFocus = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setTextinfocus(true);
        if (textFieldRef.current) {
            textFieldRef.current.select();
        }
    }, []);

    const onBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
            setTextinfocus(false);
            if (loaded && cellIsError === '' && cellnumber !== cellnumberlastsaved) {
                setDataSaved(true);
                updateRemainingWork(cellnumber);
                setCellnumberlastsaved(cellnumber);
            }
        },
        [cellnumber, cellnumberlastsaved, cellIsError, loaded, updateRemainingWork],
    );

    const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (event.key === 'Enter') {
            if (textFieldRef.current) {
                textFieldRef.current.blur();
            }
        }
    }, []);

    if (props.readonly) {
        return (
            <div className={styles.root}>
                <Text className={styles.cell}>
                    {celltext && celltext !== '0' && celltext !== '' && `${celltext}${gridInputType === 'percent' ? ' %' : ''}`}
                </Text>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <TooltipHost content={cellIsError !== '' ? cellIsError : props.readonlyReason || ''} closeDelay={250}>
                <TextField
                    componentRef={textFieldRef}
                    className="cellInput"
                    value={`${celltext !== '0' && celltext !== '' ? celltext : ''}${
                        gridInputType === 'percent' ? (!celltext || celltext === '0' || celltext === '' || textinfocus ? '' : ' %') : ''
                    }`}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onChange={onCellUpdated}
                    onKeyDown={onKeyDown}
                    disabled={props.readonly}
                    autoComplete="off"
                    style={{ textAlign: 'center' }}
                    styles={{
                        root: {},
                        fieldGroup: {
                            borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,0.75)' : 'rgba(0,0,0,0)'}`,
                            backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                            borderWidth: '2px',
                            '&:hover': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)' : 'rgba(0,0,0,0)'}`,
                                backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                            },
                            '&::after': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)' : 'rgba(96,97,170,1)'}`,
                            },
                        },
                        field: {
                            textAlign: 'right',
                            paddingRight: theme.spacing.s1,
                            paddingLeft: 7,
                        },
                    }}
                />
            </TooltipHost>
        </div>
    );
};

const getStyles = makeStyles(theme => ({
    root: [
        'cellInner',
        {
            position: 'relative',
            height: '100%',
            width: '100%',
            maxWidth: constants.cellInputMaxWidthWeekOverview,
            margin: '0 auto',
            display: 'flex',
        },
    ],
    cell: {
        paddingRight: theme.spacing.s1,
        paddingLeft: 7,
        fontWeight: 600,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

const isAllowedKey = (e: any) => {
    const allowed = '1234567890,.';
    if (allowed.includes(e.nativeEvent.data) || !e.nativeEvent.data) {
        return true;
    } else {
        return false;
    }
};

// Helper function for percent to hours conversion
const convertProcentToHours = (percent: number, periodDays: number, capacityPerDay: number): number => {
    return (percent / 100) * periodDays * capacityPerDay;
};
